import { useReactToPrint } from 'react-to-print';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import React, { useCallback, useMemo, useRef } from 'react';
// eslint-disable-next-line import/no-duplicates
import { default as arSALocale } from 'date-fns/locale/ar-SA';
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
// import { useReactToPrint } from 'react-to-print';

import { Button } from '@components/Button';
import { Icon } from '@components/Icon';
import { OrderDetails } from '@components/OrderDetails';
import { OrderHeader } from '@components/OrderHeader';
import { Receipt } from '@components/Receipt';
import { ScreenContent } from '@components/ScreenContent';
import { Toast } from '@components/Toast';
import { makeStyles } from '@providers/Theme';
import { useIsMobile } from '@hooks';
import { useLookupsStore } from '@stores/lookups';
import { useTranslation } from '@providers/I18n';

import { OrderActions } from './OrderActions';
import { OrderViewProps } from './types';
import { UserDetails } from './UserDetails';

const useStyles = makeStyles((theme) => ({
  root: {},
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    ...theme.mixins.padding(0.5),
  },
  card: {
    marginTop: theme.spacing(2),
    ...theme.mixins.padding(2),
  },
  printBillButton: {
    color: theme.palette.cool[700],
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    flexDirection: 'row',
    padding: theme.spacing(2),
  },
  details: {
    padding: theme.spacing(2),
  },
  toast: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
    marginVertical: theme.spacing(2),
  },
  toastRoot: {
    on: {
      sm: {
        width: '100%',
      },
    },
  },
}));

export const OrderView: React.FC<OrderViewProps> = ({
  order,
  loading,
  onBackPress,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const { value } = useLookupsStore();

  const lang = i18n.language;
  const receiptRef = useRef(null);

  // get rejected reason for reject order
  const rejectedReason = useMemo(() => {
    const updateDetails = order?.orderMessage?.data?.updateDetails;
    if (updateDetails) {
      const reason = value?.rejectionReasons.filter(
        (item) => item.value === updateDetails.actionType,
      );
      return reason?.length
        ? lang === 'ar'
          ? reason[0].titleAr
          : reason[0].titleEn
        : '';
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, order?.orderMessage?.data?.updateDetails]);

  const rejectedTime = useMemo(() => {
    const updateDetails = order?.orderMessage?.data?.updateDetails;
    const rejectionTime = order?.orderStateTrace?.data?.rejectedTime;
    if (updateDetails && rejectionTime) {
      return (
        t('orderView.rejectedTime', 'Reject Time') +
        ': ' +
        format(new Date(rejectionTime), 'MM/dd/yyyy hh:mm a', {
          locale: lang === 'ar' ? arSALocale : undefined,
        })
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, order?.orderMessage?.data?.updateDetails]);

  const reactToPrintContent = useCallback(() => {
    return receiptRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    removeAfterPrint: true,
  });

  return (
    <ScreenContent style={styles.root}>
      <View style={styles.header}>
        {isMobile ? (
          <Button
            onPress={onBackPress}
            startIcon={
              <Icon
                name="arrow-left"
                size={24}
                color={(theme) => theme.palette.cool[700]}
              />
            }
          />
        ) : (
          <View />
        )}
        <Button
          variant="occean"
          style={styles.printBillButton}
          onPress={handlePrint}
          TypographyProps={{
            weight: 'medium',
          }}
          startIcon={
            <Icon
              name="print"
              size={24}
              color={(theme) => theme.palette.ocean[700]}
            />
          }>
          {t('orderview.header.printBill', 'Print Bill')}
        </Button>
      </View>

      {loading || !order  || !order.orderMessage ? (
        <View style={styles.container}>
          <ActivityIndicator size="large" />
        </View>
      ) : (
        <>
          <ScrollView>
            <View style={styles.details}>
              <OrderHeader order={order} />
              {rejectedReason && rejectedTime && (
                <Toast
                  type="error"
                  title={rejectedReason}
                  body={rejectedTime}
                  isDismissible={false}
                  style={{ toast: styles.toast, container: styles.toastRoot }}
                />
              )}
              <UserDetails order={order} />
              <OrderDetails categories={order?.orderMessage?.data?.categories} />
              <Receipt order={order?.orderMessage?.data} />

              <View style={{ display: 'none' }}>
                <View style={styles.details} ref={receiptRef}>
                  <OrderHeader order={order} isPrint={true} />
                  <UserDetails order={order} isPrint={true} />
                  <OrderDetails
                    categories={order?.orderMessage?.data?.categories}
                  />
                  <Receipt order={order?.orderMessage?.data} />
                </View>
              </View>
            </View>
          </ScrollView>
          <OrderActions order={order} />
        </>
      )}
    </ScreenContent>
  );
};
